import React from "react";
import PropTypes from "prop-types";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Responsive from "@amzn/meridian/responsive";
import { SMALL } from "../common/constant/BreakPoints";
import MetricBox from "./common/MetricBox";
import { useDeviceLayout } from "../common/context/DeviceLayoutContext";
import { getDocumentURL } from "../handler/ParentDataHandler";

const QualityMetricsView = function (props) {
  const spacingInset_shorthand4 = useDeviceLayout().spacingInset_shorthand4;
  const metricsData = props.metricsData;

  function getMetric(key) {
    const val = metricsData && metricsData[key];
    if (val && typeof val === "object" && val.hasOwnProperty("value")) {
      return val;
    }
    if (val === "NOT_AVAILABLE" || val === undefined || val === null) {
      return { value: "N/A", inAlert: false };
    }
    return { value: val, inAlert: false };
  }

  const incObj = metricsData && metricsData.inductDwellINCPackages;
  let incSum = 0;
  let incAlert = false;
  if (incObj && typeof incObj === "object") {
    for (const k in incObj) {
      if (incObj.hasOwnProperty(k)) {
        const v = incObj[k];
        if (v && typeof v === "object" && v.value !== undefined) {
          incSum += Number(v.value) || 0;
          if (v.inAlert) incAlert = true;
        }
      }
    }
  }
  const jackpotObj = metricsData && metricsData.inductDwellJackpotPackages;
  let jackpotSum = 0;
  let jackpotAlert = false;
  if (jackpotObj && typeof jackpotObj === "object") {
    for (const k in jackpotObj) {
      if (jackpotObj.hasOwnProperty(k)) {
        const v = jackpotObj[k];
        if (v && typeof v === "object" && v.value !== undefined) {
          jackpotSum += Number(v.value) || 0;
          if (v.inAlert) jackpotAlert = true;
        }
      }
    }
  }
  const incBoxAlert = incAlert || jackpotAlert;

  const misDivertPendingPS = getMetric("misDivertPackagesPendingPS");
  const misDivertPendingReInduct = getMetric(
    "misDivertPackagesPendingReInduct",
  );
  const misDivertResolved = getMetric("misDivertPackagesResolved");
  let misDivertPendingSum = 0;
  if (!isNaN(Number(misDivertPendingPS.value)))
    misDivertPendingSum += Number(misDivertPendingPS.value);
  if (!isNaN(Number(misDivertPendingReInduct.value)))
    misDivertPendingSum += Number(misDivertPendingReInduct.value);
  const misDivertAlert =
    (misDivertPendingPS && misDivertPendingPS.inAlert) ||
    (misDivertPendingReInduct && misDivertPendingReInduct.inAlert) ||
    (misDivertResolved && misDivertResolved.inAlert);

  const missedInductPending = getMetric("missedInductPackagesPendingInduct");
  const missedInductReinducted = getMetric("missedInductPackagesInducted");
  const missedInductAlert =
    (missedInductPending && missedInductPending.inAlert) ||
    (missedInductReinducted && missedInductReinducted.inAlert);

  const misappliedPending = getMetric("misAppliedSALPackagesPendingReInduct");
  const misappliedReinducted = getMetric("misAppliedSALPackagesReInducted");
  const misappliedAlert =
    (misappliedPending && misappliedPending.inAlert) ||
    (misappliedReinducted && misappliedReinducted.inAlert);

  const metrics = [
    {
      name: "INC Metrics",
      description:
        "It shows packages that are inducted but not stowed for more than 60 mins in each cluster, including packages diverted to the jackpot.",
      inAlert: incBoxAlert,
      subMetrics: [
        {
          name: "INC Total",
          value: incSum,
          inAlert: incAlert,
        },
        {
          name: "Jackpot Total",
          value: jackpotSum,
          inAlert: jackpotAlert,
        },
      ],
    },
    {
      name: "Misdivert Metrics",
      description:
        "Total: Packages that were marked as 'Wrong Aisle Cluster' by stowers, + ADTA process errors. DPMO: Accumulated Misdivert packages out of all packages inducted for the day. Pending PS: Misdiverted packages that is not scanned by the problem solver. Pending Reinduct: Misdiverted packages that are scanned using the PSA module, where the status has changed to 'Marked for reprocess'. Resolved: Misdiverted packages whose status is changed to one of the following: Inducted/Stowed/Staged/In Transit",
      inAlert: misDivertAlert,
      subMetrics: [
        {
          name: "Pending Total",
          value: misDivertPendingSum,
          inAlert:
            (misDivertPendingPS && misDivertPendingPS.inAlert) ||
            (misDivertPendingReInduct && misDivertPendingReInduct.inAlert),
        },
        {
          name: "Resolved Total",
          value: misDivertResolved.value,
          inAlert: misDivertResolved && misDivertResolved.inAlert,
        },
      ],
    },
    {
      name: "Missed Induct",
      description:
        "Total: Packages that failed stow with reason code 'Stow failure invalid package state'. Pending Induct: Out of the packages that missed induct that are not yet inducted. Reinducted: Out of the packages that missed induct that are successfully inducted.",
      inAlert: missedInductAlert,
      subMetrics: [
        {
          name: "Pending Induct",
          value: missedInductPending.value,
          inAlert: missedInductPending && missedInductPending.inAlert,
        },
        {
          name: "Re-Inducted",
          value: missedInductReinducted.value,
          inAlert: missedInductReinducted && missedInductReinducted.inAlert,
        },
      ],
    },
    {
      name: "Misapplied SAL",
      description:
        "This represents number of packages on which SAL was mis-applied leading to misdivert",
      inAlert: misappliedAlert,
      subMetrics: [
        {
          name: "Pending Re-Induct",
          value: misappliedPending.value,
          inAlert: misappliedPending && misappliedPending.inAlert,
        },
        {
          name: "Re-Inducted",
          value: misappliedReinducted.value,
          inAlert: misappliedReinducted && misappliedReinducted.inAlert,
        },
      ],
    },
  ];

  function handleMetricClick() {
    getDocumentURL().then((currentUrl) => {
      const urlObj = new URL(currentUrl);
      urlObj.pathname = "/station/dashboard/qualityoverview";
      window.open(urlObj.toString(), "_parent");
    });
  }

  return (
    <Responsive
      query="max-width"
      props={{
        widths: {
          default: ["grid-3", "grid-3", "grid-3", "grid-3"],
          [SMALL]: ["100%", "100%", "100%", "100%"],
        },
      }}
    >
      {function ({ widths }) {
        return (
          <Column spacing="medium" spacingInset={spacingInset_shorthand4}>
            <Row widths={widths} wrap="down" alignmentVertical="stretch">
              {metrics.map(function (item, idx) {
                return (
                  <MetricBox
                    key={idx}
                    name={item.name}
                    description={item.description}
                    inAlert={item.inAlert}
                    subMetrics={item.subMetrics}
                    onMetricClick={handleMetricClick}
                  />
                );
              })}
            </Row>
          </Column>
        );
      }}
    </Responsive>
  );
};

QualityMetricsView.propTypes = {
  metricsData: PropTypes.object.isRequired,
};

export default QualityMetricsView;



// WEBPACK FOOTER //
// ./src/components/quality/QualityMetricsView.js